import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';

import type {
  GetNarrative200,
  GetNarrativeJobs200Item,
  GetNarrativeType,
  PostNarrativeTrigger200,
  PostNarrativeTriggerBodyType,
} from '@/api/generated/lucioAPI.schemas';
import {
  getNarrative,
  getNarrativeJobs,
  postNarrativeTrigger,
} from '@/api/generated/narrative/narrative';

interface UseCustomNarrativesOptions {
  enabled?: boolean;
  /** Fires once when a job triggered during this hook's lifetime reaches a terminal status. */
  onJobSettled?: (job: GetNarrativeJobs200Item) => void;
  onTriggerError?: (error: Error) => void;
  onTriggerSuccess?: (job: PostNarrativeTrigger200) => void;
}

/**
 * Drives the lifecycle for Custom narrative windows: lists past CUSTOM jobs,
 * polls while any is in-flight, owns the "selected job" state and its per-job
 * narrative list query, and exposes `requestGeneration` for the date-range form.
 */
export function useCustomNarratives({
  enabled = true,
  onJobSettled,
  onTriggerError,
  onTriggerSuccess,
}: UseCustomNarrativesOptions = {}) {
  const queryClient = useQueryClient();
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const triggeredIdsRef = useRef<Set<string>>(new Set());

  const jobsQuery = useQuery({
    enabled,
    gcTime: 1000 * 60 * 5,
    queryFn: () => getNarrativeJobs(),
    queryKey: ['narrative', 'jobs'],
    refetchInterval: (query) => {
      const data = query.state.data ?? [];
      const hasInflight = data.some(
        (j) => j.type === 'CUSTOM' && (j.status === 'PENDING' || j.status === 'IN_PROGRESS'),
      );
      return hasInflight ? 10_000 : false;
    },
    staleTime: 0,
  });

  const customJobs = useMemo(
    () => (jobsQuery.data ?? []).filter((j) => j.type === 'CUSTOM'),
    [jobsQuery.data],
  );

  // Fire onJobSettled exactly once per terminal flip, only for jobs triggered
  // during this hook's lifetime (so a fresh mount doesn't re-emit history).
  useEffect(() => {
    if (!onJobSettled) {
      return;
    }
    for (const job of customJobs) {
      if (
        triggeredIdsRef.current.has(job.id) &&
        (job.status === 'COMPLETED' || job.status === 'FAILED')
      ) {
        triggeredIdsRef.current.delete(job.id);
        onJobSettled(job);
      }
    }
  }, [customJobs, onJobSettled]);

  const triggerMutation = useMutation({
    mutationFn: ({ endDate, startDate }: { endDate: string; startDate: string }) =>
      postNarrativeTrigger({
        endDate,
        startDate,
        type: 'CUSTOM' as PostNarrativeTriggerBodyType,
      }),
    onError: (error: Error) => {
      onTriggerError?.(error);
    },
    onSuccess: (response) => {
      const job = response;
      if (job?.id) {
        triggeredIdsRef.current.add(job.id);
      }
      queryClient.invalidateQueries({ queryKey: ['narrative', 'jobs'] });
      if (job) {
        onTriggerSuccess?.(job);
      }
    },
    retry: false,
  });

  const selectedListQuery = useInfiniteQuery<
    GetNarrative200,
    Error,
    { pageParams: Array<number>; pages: Array<GetNarrative200> },
    ReadonlyArray<unknown>,
    number
  >({
    enabled: enabled && selectedJobId !== null,
    gcTime: 1000 * 60 * 10,
    getNextPageParam: (lastPage) => {
      const pagination = lastPage?.pagination;
      if (!pagination) {
        return undefined;
      }
      const { page, totalPages } = pagination;
      return page < totalPages ? page + 1 : undefined;
    },
    initialPageParam: 1,
    queryFn: ({ pageParam = 1 }: { pageParam?: number }) => {
      if (!selectedJobId) {
        throw new Error('select(jobId) must be called before fetching');
      }
      return getNarrative({
        jobId: selectedJobId,
        limit: 10,
        page: pageParam,
        type: 'CUSTOM' as GetNarrativeType,
      });
    },
    queryKey: ['narrative', 'list', 'CUSTOM', selectedJobId],
    staleTime: 1000 * 60 * 5,
  });

  const selectedNarratives =
    selectedListQuery.data?.pages.flatMap((page) => page.narratives ?? []) ?? [];

  const select = (jobId: string) => setSelectedJobId(jobId);
  const back = () => setSelectedJobId(null);
  const requestGeneration = (args: { endDate: string; startDate: string }) =>
    triggerMutation.mutateAsync(args);

  return {
    back,
    fetchNextPage: selectedListQuery.fetchNextPage,
    hasNextPage: selectedListQuery.hasNextPage ?? false,
    isFetchingNextPage: selectedListQuery.isFetchingNextPage,
    isLoadingSelected: selectedListQuery.isLoading,
    isTriggering: triggerMutation.isPending,
    jobs: customJobs,
    requestGeneration,
    select,
    selectedJobId,
    selectedNarratives,
  };
}
